import { useContext, useEffect, useState } from 'react';

import { camelize } from '@vimeo/core/libs/camelize';
import { Viewer, ViewerContext } from '@vimeo/viewer';

import { LiveChatStatusResponse } from '../utils/types';

const POLLING_DELAY: number = 4000;

/**
 * Using native fetch over vimeo query hooks because caching causes major issues there.
 */
async function fetchLiveChatStatus(roomId: number, viewer: Viewer): Promise<boolean> {
  const response = await fetch(`//${viewer.apiUrl}/live_chat/${roomId}/status`, {
    headers: {
      Authorization: `jwt ${viewer.jwt}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  if (response.ok) {
    const data: LiveChatStatusResponse = camelize(await response.json());
    return data.chatEnabled;
  } else {
    throw new Error('Failed to fetch chat status data.');
  }
}

/**
 * Custom hook to determine the visibility status of the live chat for a given video clip.
 *
 * This hook polls the live chat status for clips that are currently streaming and updates the chat's enabled state.
 * The chat is considered visible if it is enabled.
 *
 * @param roomId - The Clip Chat Room ID.
 * @returns A tuple containing a single boolean value indicating whether the live chat should be visible.
 */
export function useLiveChatStatus(roomId: number): boolean {
  const viewer: Viewer = useContext(ViewerContext) as Viewer;
  const [isEnabled, setIsEnabled] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Poll chat status for live events that are currently streamed.
  useEffect(
    function () {
      if (roomId) {
        setTimeout(async () => {
          try {
            const status = await fetchLiveChatStatus(roomId, viewer);
            setIsEnabled(status);
          } catch (error) {
            console.error(error);
          }
        });
        const intervalId = setInterval(async () => {
          try {
            setIsEnabled(await fetchLiveChatStatus(roomId, viewer));
          } catch (error) {}
        }, POLLING_DELAY);

        return () => clearInterval(intervalId);
      } else {
        setIsEnabled(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roomId, viewer?.jwt],
  );

  useEffect(
    function () {
      setIsVisible(Boolean(isEnabled));
    },
    [isEnabled],
  );

  return isVisible;
}
